<template>
  <div class="add-test-form">
    <div class="test-types">
      <Select
        class-name="test-select form-item dialog-time-picker test-types__select"
        v-model="value.type"
        :options="testItems"
        :placeholder="$t('test.testType.placeholder')"
        :title="$t('test.testType')"
        size="xsmall"
      />
      <custom-tooltip class="test-types__tooltip" popper-class="light-tooltip">
        <div slot='buttonContent'>
          <div class='popup-icon'>
            <v-icon class="material-icons-round">help</v-icon>
          </div>
        </div>
        <div slot="content">
          <slot name="tooltip">
            {{ getTestTypeInfo }}
          </slot>
        </div>
      </custom-tooltip>
    </div>

    <custom-checkbox v-if="showAdvancedOptionsFlag" v-model="value.metaData.advancedOptions.specifyPath" size="s" :title="$t('test.advanced.options.title')" color="cold-marine" />

    <div class='advanced-options' v-if='showUrlPathOptionSection'>
      <span class='label'> {{ $t('test.specify.path.section.title') }} </span>
      <url-path-input v-model='value.metaData' :portsToSuggest='portsToSuggest' @updateValid='updateValid'/>
    </div>

    <div v-if='showHostListCheckboxFlag' class='host-list-combobox'>
      <v-combobox
        dense
        outlined
        rounded
        multiple
        disable-lookup
        item-color='#5BA7ED'
        :items='value.hostOptionList'
        v-model="value.selectedHosts"
        :append-icon="'expand_more'"
      >
        <template v-slot:label>
          <div class='host-list-combobox-label'>
            {{$t("test.select.host")}}
          </div>
        </template>
      </v-combobox>
    </div>

    <custom-checkbox v-if="showDeepScanSensitiveCheckbox" v-model="value.metaData.sensitive" size="s" :title="$t('test.Sensitive')" color="cold-marine" />

    <custom-checkbox v-if="showUseProxyCheckbox" v-model="value.metaData.proxySettings.useProxy" size="s" :title="$t('test.useProxy')" color="cold-marine" />

    <div v-if='showHostListCheckboxForVulnFlag' class='host-list-combobox'>
      <v-combobox
        dense
        outlined
        rounded
        disable-lookup
        item-color='#5BA7ED'
        :items='value.hostOptionList'
        v-model="value.metaData.hostForProxyVuln"
        :append-icon="'expand_more'"
      >
        <template v-slot:label>
          <div class='host-list-combobox-label'>
            {{$t("test.select.hosts")}}
          </div>
        </template>
      </v-combobox>
    </div>

    <div class='proxy-settings' v-if='showUseProxyCheckbox && value.metaData.proxySettings.useProxy'>
      <span class='label'> {{ $t('test.proxy.settings.title') }} </span>
      <proxy-settings-input v-model='value.metaData' :type='value.type' @updateValid='updateValid'/>
    </div>

    <div class='authentication-settings' v-if='showAuthenticationSettings'>
      <span class='label'> {{ $t('test.authentication.settings.title') }} </span>
      <select-authentication-object v-model='value.metaData.authentication' :type='authenticationType'  @updateValid='updateValid' :provider='authProvider'/>
    </div>

    <custom-checkbox v-model="value.schedule" size="s" :title="$t('test.schedule').toString()" color="cold-marine"/>
    <custom-checkbox v-model="value.metaData.generateReport" size="s" :title="$t('test.generate.cyber.risk.report')" color="cold-marine" />

    <div class='custom-initiate' v-if='value.schedule'>
      <custom-datepicker
        v-model="value.startDate"
        type="date"
        :disabled='!hasLicense'
        format="dddd, LL"
        :title="$t('start.date')"
        class="form-item"
      />

      <div v-if="!value.isAllDay" class="time-range d-flex flex-row">
        <custom-timepicker
          v-model="value.startTime"
          :disabled='!hasLicense'
          :title="$t('test.startTime')"
          class="time-picker start-time form-item"
        />
      </div>

      <div class="all-day">
        <custom-checkbox v-model="value.isAllDay" size="s" :title="$t('test.allDay')" color="cold-marine" :disabled='!hasLicense'/>
      </div>

      <timezone-picker v-model="value.timezone" class="timezone form-item dialog-time-picker" :disabled='!hasLicense'/>

      <Select
        class-name="test-select form-item dialog-time-picker"
        v-model="value.frequency"
        :disabled='!hasLicense'
        :options="repeatItems"
        :title="testRepeatSelectTitle"
        size="xsmall"
      />

      <repeat-custom-subform v-if="showCustomRepeatSubform" v-model="value.customRepeat" :disabled='!hasLicense'/>

      <span v-if='!hasLicense'>
        <v-alert
          text
          dense
          color="#e0255e"
          icon="info"
          border="left"
          class='not-valid-message'
        >
          {{ $t('license.disable.schedule.scans') }}
        </v-alert>
      </span>
    </div>
    <div v-if="showNoDiscoveryNotice">
      <v-alert
        text
        dense
        color="#e0255e"
        icon="info"
        border="left"
        class='not-valid-message'
      >
        {{$t('scan.disable.no.discovery')}}
      </v-alert>
    </div>
    <div v-if="showNoDomainValidationNotice">
      <v-alert
        text
        dense
        color="#e0255e"
        icon="info"
        border="left"
        class='not-valid-message'
      >
        {{$t('scan.disable.no.domain.validation')}}
      </v-alert>
    </div>
<!--    <YesNoPopup-->
<!--      title='cloud.services.disabled.popup.header'-->
<!--      :visible-popup='showFeatureTogglePopup'-->
<!--      :showNoButton='false'-->
<!--      yes-caption='cloud.services.disabled.popup.ok'-->
<!--      message='cloud.services.disabled.popup.message'-->
<!--      @clicked='showFeatureTogglePopup = false'-->
<!--    />-->
  </div>
</template>

<script>
import moment from 'moment';
import {
  DATA_LEAKS_TYPES,
  TEST_REPEAT_STATUS, TEST_TYPES_ACCOUNTS,
  TEST_TYPES_CLOUD_ASSETS,
  TEST_TYPES_HOSTS, TEST_TYPES_INFO,
  TEST_TYPES_SUBDOMAINS,
  TEST_TYPES_TLDS,
} from '@/constants/projects';
import CustomCheckbox from '@/components/common/CustomCheckbox.vue';
import Select from '@/components/common/Select.vue';
import CustomDatepicker from '@/components/datepicker/CustomDatepicker.vue';
import CustomTimepicker from '@/components/datepicker/CustomTimepicker.vue';
import TimezonePicker from '@/components/datepicker/TimezonePicker.vue';
import RepeatCustomSubform from '@/components/form-elements/RepeatCustomSubform.vue';
import ProxySettingsInput from '@/components/common/ProxySettingsInput.vue';
import SelectAuthenticationObject from '@/components/common/SelectAuthenticationObject.vue';
import { AUTHENTICATION_OBJECT_TYPES, AUTHENTICATION_TO_PROVIDER } from '@/constants/authenticationObject';
import UrlPathInput from '@/components/common/UrlPathInput.vue';
import CustomTooltip from '@/components/common/CustomTooltip.vue';
import {CONFIGURATION} from '@/constants/configuration';

export default {
  name: 'AddTestForm',
  components: {
    UrlPathInput,
    SelectAuthenticationObject,
    ProxySettingsInput,
    CustomCheckbox,
    Select,
    CustomDatepicker,
    CustomTimepicker,
    TimezonePicker,
    RepeatCustomSubform,
    CustomTooltip,
    // YesNoPopup,
  },
  props: {
    value: {
      type: Object,
      required: true,
    },
    portsToSuggest: [],
    testTypes: {},
    enableCloudComplianceFeature: {
      type: Boolean,
      default: false,
      required: false,
    },
    hasLicense: {
      type: Boolean,
      default: false,
      required: false,
    },
    noDiscoveryFinishedForTLD: {
      type: Boolean,
      default: false,
      required: false,
    },
    isDomainValidated: {
      type: Boolean,
      default: false,
      required: false,
    },
    website: {},
    tab: {
      type: String,
      default: '',
      required: false,
    },
  },
  data() {
    return {
      date: '',
      // showFeatureTogglePopup: false,
    };
  },
  methods: {
    updateValid(valid) {
      this.value.valid = valid;
    },
  },
  watch: {
    'value.schedule': {
      handler() {
        this.$emit('switchSubmitButtonLabel', this.value.schedule);
      },
      deep: true,
    },
    'value.metaData.proxySettings.useProxy': {
      handler() {
        if (this.value.metaData.proxySettings.useProxy) {
          this.value.valid = false;
          if (this.value.type === TEST_TYPES_SUBDOMAINS.vuln_infra && this.value.hostOptionList.length === 1) {
            this.value.metaData.hostForProxyVuln = [this.value.hostOptionList[0]];
          }
        } else {
          this.value.valid = true;
        }
      },
      deep: true,
    },
    'value.metaData.authentication.useAuthentication': {
      async handler() {
        if (this.value.metaData.authentication.useAuthentication) {
          this.value.valid = false;
        } else {
          this.value.valid = true;
        }
      },
      deep: true,
    },
    'value.type': {
      handler() {
        if (this.value.type === TEST_TYPES_TLDS.cloud_compliance && !this.enableCloudComplianceFeature) {
          this.$emit('showFeatureTogglePopup');
          this.$emit('cancel');
          // this.showFeatureTogglePopup = true;
        }
        if (this.value.type !== TEST_TYPES_HOSTS.deepscan && this.value.type !== TEST_TYPES_HOSTS.autoexplotation) {
          this.value.selectedHosts = [];
        } else if (this.value.hostOptionList.length === 1) {
          this.value.selectedHosts = [this.value.hostOptionList[0]];
        }
        this.value.metaData.proxySettings.useProxy = false;
        this.value.metaData.authentication.useAuthentication = false;
        if (this.value.type === TEST_TYPES_ACCOUNTS.cloud_discovery || this.value.type === TEST_TYPES_ACCOUNTS.cloud_compliance) {
          this.value.metaData.authentication.useAuthentication = true;
        }
      },
      deep: true,
    },
  },
  computed: {
    getTestTypeInfo() {
      if (this.value.type === TEST_TYPES_CLOUD_ASSETS.vuln_infra && Object.values(CONFIGURATION.SOURCE_TYPE).includes(this.website.source)) {
        return TEST_TYPES_INFO.cloud_vuln_infra;
      }
      if (this.value.type !== '') {
        return TEST_TYPES_INFO[this.value.type];
      }
      if (this.tab === 'cloud' || this.tab === 'infrastructure') {
        return TEST_TYPES_INFO.defaultSubdomain;
      }
      return TEST_TYPES_INFO.defaultTLD;
    },
    testRepeatSelectTitle() {
      return this.$t('test.repeat.title');
    },
    showAdvancedOptionsFlag() {
      return this.value.type === TEST_TYPES_SUBDOMAINS.vuln_infra;
    },
    showUrlPathOptionSection() {
      return this.showAdvancedOptionsFlag && this.value.metaData.advancedOptions.specifyPath;
    },
    showHostListCheckboxFlag() {
      return this.value.hostOptionList?.length &&
        (Object.values(TEST_TYPES_HOSTS).includes(this.value.type));
    },
    showHostListCheckboxForVulnFlag() {
      return this.value.hostOptionList?.length &&
          (this.value.type === TEST_TYPES_SUBDOMAINS.vuln_infra && this.value.metaData.proxySettings.useProxy);
    },
    showLeakDataTypeComboboxFlag() {
      return this.value.type === TEST_TYPES_TLDS.dataleaks;
    },
    showDeepScanSensitiveCheckbox() {
      return this.value.type === TEST_TYPES_HOSTS.deepscan;
    },
    showUseProxyCheckbox() {
      return (this.value.type === TEST_TYPES_SUBDOMAINS.vuln_infra && !(Object.keys(CONFIGURATION.SOURCE_TYPE).includes(this.website.source))) ||
        (this.showHostListCheckboxFlag && this.value.type === TEST_TYPES_HOSTS.deepscan);
    },
    showAuthenticationSettings() {
      return this.value.type === TEST_TYPES_ACCOUNTS.cloud_discovery || this.value.type === TEST_TYPES_ACCOUNTS.cloud_compliance;
    },
    authenticationType() {
      if (this.value.type === TEST_TYPES_ACCOUNTS.cloud_discovery || this.value.type === TEST_TYPES_ACCOUNTS.cloud_compliance) {
        return [AUTHENTICATION_OBJECT_TYPES.AWS, AUTHENTICATION_OBJECT_TYPES.GCP, AUTHENTICATION_OBJECT_TYPES.AZURE];
      }
      if (this.value.type === TEST_TYPES_HOSTS.deepscan || this.value.type === TEST_TYPES_SUBDOMAINS.vuln_infra) {
        return AUTHENTICATION_OBJECT_TYPES.USER_PASSWORD;
      }
      return null;
    },
    authProvider() {
      return (this.website?.settingsMetaData?.provider) ? AUTHENTICATION_TO_PROVIDER[this.website.settingsMetaData.provider] : '';
    },
    typesOfDataLeaks() {
      return Object.values(DATA_LEAKS_TYPES).map(type => this.$t(`data.leak.type.${type}`));
    },
    testItems() {
      let testTypesFormatted = this.testTypes.map(test => ({ value: test, title: this.$t(`test.testType.${test}`) }));
      if (this.testTypes.includes(TEST_TYPES_TLDS.dataleaks) || this.testTypes.includes(TEST_TYPES_TLDS.sitemap)) {
        testTypesFormatted = this.testTypes.map(test => ({
          value: test,
          title: this.$t(`test.testType.${test}`),
          disabled: (test === TEST_TYPES_TLDS.dataleaks || test === TEST_TYPES_TLDS.sitemap) ? this.noDiscoveryFinishedForTLD : false,
        }));
      } else if (this.testTypes.includes(TEST_TYPES_SUBDOMAINS.vuln_infra) || this.testTypes.includes(TEST_TYPES_HOSTS.deepscan) || this.testTypes.includes(TEST_TYPES_HOSTS.autoexplotation)) {
        testTypesFormatted = this.testTypes.map(test => ({
          value: test,
          title: this.$t(`test.testType.${test}`),
          disabled: (test === TEST_TYPES_SUBDOMAINS.vuln_infra || test === TEST_TYPES_HOSTS.deepscan || test === TEST_TYPES_HOSTS.autoexplotation) ? !this.isDomainValidated : false,
        }));
      }
      return testTypesFormatted;
    },
    repeatItems() {
      const startDate = moment(this.value.startDate);
      const weekday = startDate.format('dddd');
      const weekdayNumber = Math.ceil(startDate.date() / 7);
      const date = startDate.format('D MMMM');

      const translationsMap = {
        never: this.$t('test.repeat.never'),
        daily: this.$t('test.repeat.daily'),
        weekly: this.$t('test.repeat.weekly', { weekday }),
        monthly: this.$t('test.repeat.monthly', {
          weekdayNumber: this.$t(`test.repeat.monthly.${weekdayNumber}`),
          weekday,
        }),
        annually: this.$t('test.repeat.annually', { date }),
        weekday: this.$t('test.repeat.weekday'),
        custom: this.$t('test.repeat.custom'),
      };

      return Object.values(TEST_REPEAT_STATUS).map(item => ({
        value: item,
        title: translationsMap[item] || item,
      }));
    },
    showCustomRepeatSubform() {
      return this.value.frequency === TEST_REPEAT_STATUS.custom;
    },
    showNoDiscoveryNotice() {
      return this.noDiscoveryFinishedForTLD && (this.value.type === TEST_TYPES_TLDS.sitemap || this.value.type === TEST_TYPES_TLDS.dataleaks);
    },
    showNoDomainValidationNotice() {
      return !this.isDomainValidated && (this.value.type === TEST_TYPES_SUBDOMAINS.vuln_infra || this.value.type === TEST_TYPES_HOSTS.autoexplotation || this.value.type === TEST_TYPES_HOSTS.deepscan);
    },
  },
};
</script>

<style lang='scss'>
@import '~@/assets/styles/design-system';

.add-test-form {
  .v-text-field--outlined {
    input {
      left: 0;
      position: absolute;
    }
  }
}
.v-text-field fieldset, .v-text-field .v-input__control {
  color: #657D94;
  border-radius: 2px;
}
.v-select__selections{
  margin-left: 5px;
}
.v-select__slot {
  background-color: transparent;
}
.v-text-field--outlined input{
  padding: 0 !important;
}
.v-list-item {
  min-height: 30px;
}
.v-list-item__title {
  font-size: $font-size-base !important;
  font-family: $font-family-regular;
}
.v-list-item__action {
  margin-right: 15px !important;
}
.v-select__selections {
  margin-left: 15px;
  border-radius: 2px !important;
  border-color: #5BA7ED;
  color: var(--text-primary);
  font-family: $font-family-regular;
  font-size: $font-size-base;
}
.dialog-time-picker {
  .el-input__suffix {
    height: auto;
    top: -12px;
  }
}
.test-types {
  &__select {
    .el-select {
      width: 270px;
    }
  }
}
</style>
<style lang="scss" scoped>
@import '~@/assets/styles/design-system';
.v-combobox {
  margin-bottom: rem(15);
}
.add-test-form {
  .test-types {
    display: flex;
    &__tooltip {
      margin-left: rem(-25);
    }
  }
  .proxy-settings {
    .label {
      text-transform: uppercase;
      color: var(--text-secondary);
      font-family: $font-family-regular;
      font-size: rem(10);
      letter-spacing: rem(0.8);
      line-height: rem(10);
      margin-bottom: rem(15);
    }
  }
  .authentication-settings {
    margin-bottom: rem(15);
  }
  .test-select,
  .timezone {
    margin-right: 0;
  }

  .data-leak-type-combobox {
    display: flex;
    flex-wrap: wrap;
    margin-right: rem(10);
    margin-bottom: rem(15);
    width: 100%;
    justify-content: space-between;
  }
  .not-valid-message {
    font-size: rem(10);
    font-weight: 600;
    letter-spacing: rem(1.12);
    line-height: rem(16);
  }
  .advanced-options {
    .label {
      text-transform: uppercase;
      color: var(--text-secondary);
      font-family: $font-family-regular;
      font-size: rem(10);
      letter-spacing: rem(0.8);
      line-height: rem(10);
      margin-bottom: rem(10);
    }
    .v-text-field {
      margin-top: rem(4) !important;
    }
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    justify-content: space-between;
    margin-bottom: rem(15);
    margin-top: rem(25);
  }
  .host-list-combobox {
    display: flex;
    flex-wrap: wrap;
    margin-right: rem(10);
    margin-bottom: rem(15);
    margin-top: rem(25);
    width: 100%;
    justify-content: space-between;
    &-label{
      color: var(--text-tertiary);
      font-family: $font-family-regular;
      font-size: rem(10);
      letter-spacing: rem(0.8);
      line-height: rem(10);
      margin-bottom: rem(5);
      top: rem(-15) !important;
    }
    .v-text-field {
      margin-top: 0 !important;
    }
    .v-combobox {
      height: rem(28) !important;
      .v-icon {
        color: var(--text-disabled);
      }
      .theme--light.v-input {
        color: var(--brand-primary);
      }
    }
  }

  .select ::v-deep span {
    text-transform: uppercase;
  }

  .form-item {
    margin-bottom: rem(15);
  }

  .el-date-editor.el-input {
    width: rem(300) !important;
  }

  .time-range {
    .start-time {
      margin-right: rem(8);
    }

    .time-picker {
      flex: 1;
    }
  }
}
</style>
<style lang='scss'>
.add-test-form{
  .v-icon.v-icon {
    color: #C0C4CC !important;
    font-size: 22px;
  }
  .v-alert__icon.v-icon {
    color: var(--status-error) !important ;
  }
  .v-text-field--outlined.v-input--dense .v-label--active{
    position: unset !important;
    font-size: 10px !important;
    transform: translateY(-18px) scale(1) !important;
  }
}
.host-list-combobox{
  .v-text-field--outlined.v-input--dense .v-label {
    top: -15px !important;
  }
  .v-select__selections{
    margin-left: -60px;
  }
  &.hover {
    cursor: pointer;
  }
}

</style>
